import { useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Group,
  Loader,
  Modal,
  NumberInput,
  Select,
  Stack,
  TextInput,
} from '@mantine/core';
import SmartCreateDrawer from 'src/secret-menu/smart-create/SmartCreateDrawer';
import { SortableColumn } from 'src/common/components/table/types';
import { BillingType, Company, MigrationStatus } from '../hooks/useCompanies';
import {
  CreateCompanyInput,
  useCreateCompanyMutation,
} from 'src/secret-menu/hooks/useCreateCompanyMutation';
import { useUpdateCompanyMutation } from 'src/secret-menu/hooks/useUpdateCompanyMutation';
import { useDisclosure } from '@mantine/hooks';
import { useForm } from '@mantine/form';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { SimpleTable } from 'src/common/components/table/SimpleTable';
import { useSharedFeedIdentifiersConfirmation } from './useSharedFeedIdentifiersConfirmation';

const billingTypeOptions = [
  { value: '', label: 'None' },
  { value: BillingType.FIXED, label: 'Fixed' },
  { value: BillingType.PER_UNIT, label: 'Per unit' },
];

type EditFormValues = {
  name: string;
  finio_firm_name: string;
  fca_firm_reference_numbers: string;
  is_internal: boolean;
  billing_type: BillingType | '';
  billing_amount: number | null;
};

export default function CompaniesTab() {
  const [createModalOpened, { open: openCreateModal, close: closeCreateModal }] =
    useDisclosure(false);
  const [editModalOpened, { open: openEditModal, close: closeEditModal }] = useDisclosure(false);
  const [smartCreateOpened, { open: openSmartCreate, close: closeSmartCreate }] =
    useDisclosure(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);

  const { createCompany, isLoading: isCreating } = useCreateCompanyMutation();
  const { updateCompany, isLoading: isUpdating } = useUpdateCompanyMutation();
  const { submitWithConfirmation, confirmationModal } = useSharedFeedIdentifiersConfirmation();

  const createForm = useForm({
    mode: 'uncontrolled',
    initialValues: {
      name: '',
      fca_firm_reference_number: '',
      billing_type: '' as BillingType | '',
      billing_amount: null as number | null,
    },
    validate: {
      name: validateName,
    },
  });

  const editForm = useForm<EditFormValues>({
    mode: 'uncontrolled',
    initialValues: {
      name: '',
      finio_firm_name: '',
      fca_firm_reference_numbers: '',
      is_internal: false,
      billing_type: '' as BillingType | '',
      billing_amount: null as number | null,
    },
    validate: {
      name: validateName,
    },
  });

  const handleCreateCompany = async (values: {
    name: string;
    fca_firm_reference_number: string;
    billing_type: BillingType | '';
    billing_amount: number | null;
  }) => {
    try {
      const input: CreateCompanyInput = {
        name: values.name,
        fca_firm_reference_number: values.fca_firm_reference_number,
        billing_type: values.billing_type || null,
        billing_amount: values.billing_amount,
      };
      const created = await submitWithConfirmation(confirm =>
        createCompany({ ...input, confirm_shared_feed_identifiers: confirm }),
      );
      if (!created) return;
      createForm.reset();
      closeCreateModal();
    } catch (error) {
      notifyAndLogError(
        'Failed to create company',
        'An error occurred while creating the company.',
        error,
      );
    }
  };

  const handleEditCompany = (company: Company) => {
    setSelectedCompany(company);
    editForm.setValues({
      name: company.name,
      finio_firm_name: company.finioFirmName || '',
      fca_firm_reference_numbers:
        company.fcaFirmReferenceNumbers?.map(n => n.number).join(', ') || '',
      is_internal: company.isInternal,
      billing_type: company.billingType || '',
      billing_amount: company.billingAmount ?? null,
    });
    openEditModal();
  };

  const handleUpdateCompany = async (values: EditFormValues) => {
    if (!selectedCompany) return;

    try {
      const fcaNumbers = values.fca_firm_reference_numbers
        .split(',')
        .map(n => n.trim())
        .filter(n => n.length > 0)
        .map(number => {
          const existing = selectedCompany.fcaFirmReferenceNumbers?.find(f => f.number === number);
          return { id: existing?.id, number };
        });

      const updated = await submitWithConfirmation(confirm =>
        updateCompany({
          companyId: selectedCompany.id,
          name: values.name,
          finioFirmName: values.finio_firm_name || null,
          fcaFirmReferenceNumbers: fcaNumbers,
          isInternal: values.is_internal,
          billingType: values.billing_type || null,
          billingAmount:
            !values.billing_amount && values.billing_amount !== 0 ? null : values.billing_amount,
          confirmSharedFeedIdentifiers: confirm,
        }),
      );
      if (!updated) return;
      editForm.reset();
      closeEditModal();
      setSelectedCompany(null);
    } catch (error) {
      notifyAndLogError(
        'Failed to update company',
        'An error occurred while updating the company.',
        error,
      );
    }
  };

  const columns: SortableColumn<Company>[] = [
    { accessor: 'name', title: 'Name', sortable: true },
    { accessor: 'tenantName', title: 'Tenant name', sortable: true },
    {
      accessor: 'fcaFirmReferenceNumbers',
      title: 'FRN',
      render: company => company.fcaFirmReferenceNumbers?.map(n => n.number).join('  |  ') ?? '-',
    },
    { accessor: 'clientsCount', title: 'Clients count', sortable: true },
    {
      accessor: 'migrationStatus',
      title: 'Migration status',
      sortable: true,
      render: company => (
        <Badge color={getMigrationStatusColor(company.migrationStatus)}>
          {getMigrationStatusLabel(company.migrationStatus)}
        </Badge>
      ),
    },
    {
      accessor: 'isInternal',
      title: 'Internal',
      sortable: true,
      sortValueGetter: (company: Company) => (company.isInternal ? 'Yes' : 'No'),
      render: company => (company.isInternal ? 'Yes' : 'No'),
    },
    {
      accessor: 'actions',
      title: 'Actions',
      render: company => (
        <Button
          size="xs"
          variant="light"
          onClick={e => {
            e.stopPropagation();
            handleEditCompany(company);
          }}
        >
          Edit
        </Button>
      ),
    },
  ];

  return (
    <Box>
      <SmartCreateDrawer opened={smartCreateOpened} close={closeSmartCreate} />
      {confirmationModal}

      <Modal onClose={closeCreateModal} opened={createModalOpened} title="Create company">
        {isCreating ? (
          <Loader id="loader" />
        ) : (
          <form onSubmit={createForm.onSubmit(values => handleCreateCompany(values))}>
            <TextInput
              required
              label="Company name"
              placeholder="eg. Afternoon limited"
              key={createForm.key('name')}
              {...createForm.getInputProps('name')}
            />
            <TextInput
              required
              label="FCA firm reference number"
              placeholder="eg. 1234567"
              key={createForm.key('fca_firm_reference_number')}
              {...createForm.getInputProps('fca_firm_reference_number')}
            />
            <Select
              mt="md"
              label="Billing type"
              placeholder="Select billing type"
              data={billingTypeOptions}
              key={createForm.key('billing_type')}
              {...createForm.getInputProps('billing_type')}
            />
            <NumberInput
              mt="md"
              label="Billing amount (£)"
              placeholder="eg. 10.00"
              decimalScale={2}
              min={0}
              key={createForm.key('billing_amount')}
              {...createForm.getInputProps('billing_amount')}
            />
            <Button mt="md" type="submit">
              Submit
            </Button>
          </form>
        )}
      </Modal>

      <Modal
        onClose={closeEditModal}
        opened={editModalOpened}
        title={`Edit company - ${selectedCompany?.name ?? ''}`}
        size="lg"
      >
        {isUpdating ? (
          <Loader id="edit-loader" />
        ) : (
          <form onSubmit={editForm.onSubmit(values => handleUpdateCompany(values))}>
            <Stack gap="md">
              <TextInput
                required
                label="Company name"
                placeholder="eg. Afternoon limited"
                key={editForm.key('name')}
                {...editForm.getInputProps('name')}
              />
              <TextInput
                label="FINIO firm name"
                placeholder="eg. FINIO firm name"
                key={editForm.key('finio_firm_name')}
                {...editForm.getInputProps('finio_firm_name')}
              />
              <TextInput
                label="FCA firm reference numbers"
                description="Comma-separated list of 6-7 digit numbers"
                placeholder="eg. 123456, 1234567"
                key={editForm.key('fca_firm_reference_numbers')}
                {...editForm.getInputProps('fca_firm_reference_numbers')}
              />
              <Checkbox
                label="Internal company"
                key={editForm.key('is_internal')}
                {...editForm.getInputProps('is_internal', { type: 'checkbox' })}
              />
              <Select
                label="Billing type"
                placeholder="Select billing type"
                data={billingTypeOptions}
                key={editForm.key('billing_type')}
                {...editForm.getInputProps('billing_type')}
              />
              <NumberInput
                label="Billing amount (£)"
                placeholder="eg. 10.00"
                decimalScale={2}
                min={0}
                key={editForm.key('billing_amount')}
                {...editForm.getInputProps('billing_amount')}
              />
              <Group>
                <Button type="submit">Save</Button>
                <Button variant="light" onClick={closeEditModal}>
                  Cancel
                </Button>
              </Group>
            </Stack>
          </form>
        )}
      </Modal>

      <SimpleTable<Company>
        resource="admin/companies"
        columns={columns}
        title="Companies"
        showCount
        enableSearch
        pageSize={20}
        primaryButton={{ label: 'Create', onClick: openCreateModal }}
        titleSuffix={
          <Button variant="light" onClick={openSmartCreate} ml="xs">
            Smart create
          </Button>
        }
      />
    </Box>
  );
}

const validateName = (value: string): string | null => {
  if (value.length > 50) {
    return 'Name must be at most 50 characters';
  }
  if (!/[A-Za-z0-9]/.test(value)) {
    return 'Name must contain at least one alphanumeric character';
  }
  return null;
};

const getMigrationStatusColor = (status: MigrationStatus) => {
  switch (status) {
    case MigrationStatus.COMPLETED:
      return 'green';
    case MigrationStatus.IN_PROGRESS:
      return 'blue';
    case MigrationStatus.FAILED:
      return 'red';
    case MigrationStatus.NOT_STARTED:
    default:
      return 'gray';
  }
};

const getMigrationStatusLabel = (status: MigrationStatus) => {
  switch (status) {
    case MigrationStatus.NOT_STARTED:
      return 'Not started';
    case MigrationStatus.IN_PROGRESS:
      return 'In progress';
    case MigrationStatus.COMPLETED:
      return 'Completed';
    case MigrationStatus.FAILED:
      return 'Failed';
    default:
      return status;
  }
};
